import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Heading,
  HStack,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import NetworkOrganizationForm, {
  emptyNetworkOrganizationForm,
  toNetworkOrganizationFormValues,
} from "./NetworkOrganizationForm";
import { getNetworkOrganization, updateNetworkOrganization } from "./networkApi";

const NetworkOrganizationEdit = () => {
  const { networkId, organizationId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [form, setForm] = useState(emptyNetworkOrganizationForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const detailPath = `/app/network/${networkId}/organizations/${organizationId}`;

  const loadOrganization = useCallback(async () => {
    setLoading(true);
    try {
      const organization = await getNetworkOrganization(networkId, organizationId);
      setForm(toNetworkOrganizationFormValues(organization));
    } catch (error) {
      console.error("Error loading network organization:", error);
      toast({ title: "Could not load school", status: "error", duration: 4000, isClosable: true });
      navigate(`/app/network/${networkId}/organizations`);
    } finally {
      setLoading(false);
    }
  }, [navigate, networkId, organizationId, toast]);

  useEffect(() => {
    loadOrganization();
  }, [loadOrganization]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "School name is required.";
    if (!form.email.trim()) nextErrors.email = "Email is required.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      await updateNetworkOrganization(networkId, organizationId, form);
      toast({ title: "School updated", status: "success", duration: 3000, isClosable: true });
      navigate(detailPath);
    } catch (error) {
      console.error("Error updating network organization:", error);
      toast({ title: "Could not update school", status: "error", duration: 4000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <HStack justify="center" py={12}><Spinner size="lg" /></HStack>;
  }

  return (
    <Box p={{ base: 4, md: 6 }} maxW="container.md" mx="auto">
      <Heading size="lg">Edit school</Heading>
      <Text color="gray.500" mb={5}>Update network-scoped school details.</Text>

      <NetworkOrganizationForm
        form={form}
        errors={errors}
        isSubmitting={saving}
        submitLabel="Save"
        onCancel={() => navigate(detailPath)}
        onChange={handleChange}
        onSubmit={handleSubmit}
      />
    </Box>
  );
};

export default NetworkOrganizationEdit;
